import React from 'react';
import { motion } from 'framer-motion';
import { ArrowsClockwise, Trash, Recycle } from '@phosphor-icons/react';

/**
 * RECYCLER BOT — MAGENTA SPECTRAL CLASS
 * SFUI-004 Rev 3 Bot Differentiation Standard
 * 
 * The Recycler manages Federation entropy:
 * - Node lifecycle management
 * - System renewal processes
 * - Data hygiene and reclamation
 * 
 * Spectral Treatment: MAGENTA
 * - hue-rotate(300deg) saturate(1.4) brightness(0.95)
 * - Represents transformation, dissolution, renewal
 */

const RecyclerBot = ({ 
  size = 80, 
  showLabel = true, 
  showStatus = false,
  animated = true,
  className = ''
}) => {
  return (
    <div className={`flex flex-col items-center gap-3 ${className}`}>
      {/* Recycler Bot Avatar */}
      <motion.div
        animate={animated ? {
          rotate: [0, 5, -5, 0],
          scale: [1, 1.03, 1]
        } : {}}
        transition={{
          duration: 3,
          repeat: animated ? Infinity : 0,
          ease: "easeInOut"
        }}
        className="relative"
        style={{ width: size, height: size }}
      >
        {/* Pulsing MAGENTA Glow */}
        <motion.div
          animate={animated ? {
            scale: [1, 1.2, 1],
            opacity: [0.4, 0.7, 0.4]
          } : {}}
          transition={{
            duration: 2,
            repeat: animated ? Infinity : 0,
            ease: "easeInOut"
          }}
          className="absolute inset-0 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(255,0,255,0.8) 0%, rgba(224,64,251,0.5) 50%, transparent 70%)',
            filter: 'blur(15px)'
          }}
        />
        
        {/* Recycler Face with MAGENTA Spectral Filter */}
        <div 
          className="relative w-full h-full rounded-full overflow-hidden shadow-2xl border-2 border-[#FF00FF]/60"
          style={{
            boxShadow: '0 0 30px rgba(255, 0, 255, 0.8), 0 0 60px rgba(255, 0, 255, 0.5)'
          }}
        >
          <img 
            src="https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/a19508c2ed2b476ca3b35ebaa2b50539_IMG_4165.jpeg"
            alt="The Recycler — Federation Entropy Handler"
            className="w-full h-full object-cover"
            style={{
              filter: 'hue-rotate(300deg) saturate(1.4) brightness(0.95)'
            }}
          />
        </div>

        {/* Recycling Icon Overlay */}
        <motion.div
          animate={animated ? { rotate: 360 } : {}}
          transition={{
            duration: 8,
            repeat: animated ? Infinity : 0,
            ease: "linear"
          }}
          className="absolute -bottom-1 -right-1 w-8 h-8 bg-[#FF00FF] rounded-full flex items-center justify-center border-2 border-[#030305] shadow-lg"
        >
          <ArrowsClockwise size={16} weight="bold" className="text-white" />
        </motion.div>
      </motion.div>

      {/* Label */}
      {showLabel && (
        <div className="text-center">
          <p className="text-sm font-bold text-[#FF00FF] mb-0.5">
            The Recycler
          </p>
          <p className="text-xs text-gray-400">
            Entropy Handler
          </p>
        </div>
      )}

      {/* Status Indicator */}
      {showStatus && (
        <motion.div
          animate={{
            opacity: [0.6, 1, 0.6]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="flex items-center gap-2 px-3 py-1.5 bg-[#FF00FF]/20 border border-[#FF00FF]/50 rounded-full"
        >
          <div className="w-2 h-2 bg-[#FF00FF] rounded-full animate-pulse" />
          <span className="text-xs text-[#FF00FF] font-bold mono">
            RECYCLE ACTIVE
          </span>
        </motion.div>
      )}
    </div>
  );
};

export default RecyclerBot;
